/*
 * Shop-wide behaviour, ported from the client components of the Next.js site.
 * Plain JavaScript, no build step. Everything starts from data-* attributes in the Liquid,
 * and every form still works without it (the bag falls back to the cart page).
 */

const { routes, strings } = window.theme;

/** "EGP 1,250" (formatPrice in lib/commerce/format.ts). Amounts arrive in cents. */
function formatPrice(cents) {
  return `EGP ${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/** "A", "A and B", "A, B and C" (listNames in lib/commerce/catalog.ts). */
function listNames(names) {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function format(template, values) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? "");
}

/* ------------------------------------------------------------------------------------------------
 * Header: the hanging tab folds to the emblem once the page scrolls (Header.tsx).
 * ---------------------------------------------------------------------------------------------- */

function initHeader() {
  const header = document.querySelector("[data-header]");
  if (!header) return;
  const onScroll = () => header.toggleAttribute("data-scrolled", window.scrollY > 24);
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
}

/* Mobile menu: a full-height native <dialog>. Closes on its button, a link or the backdrop. */
function initMenu() {
  const menu = document.querySelector("[data-menu]");
  if (!menu) return;
  document.querySelector("[data-menu-open]")?.addEventListener("click", () => menu.showModal());
  menu.querySelector("[data-menu-close]")?.addEventListener("click", () => menu.close());
  menu.addEventListener("click", (event) => {
    if (event.target === menu || event.target.closest("a")) menu.close();
  });
}

/* ------------------------------------------------------------------------------------------------
 * The bag (bag.tsx and BagDrawer.tsx). Shopify keeps the cart; after each change we ask it to
 * render the drawer section again and swap the body, so prices and lines are always its own.
 * ---------------------------------------------------------------------------------------------- */

const SECTION = "bag-drawer";

const bag = {
  dialog: null,

  init() {
    this.dialog = document.querySelector("[data-bag]");

    document.addEventListener("click", (event) => {
      const opener = event.target.closest("[data-bag-open]");
      if (opener && this.dialog) {
        event.preventDefault();
        this.open();
        return;
      }
      const remove = event.target.closest("[data-bag-remove]");
      if (remove) {
        this.change(remove.dataset.bagRemove, 0);
        return;
      }
      if (event.target.closest("[data-bag-close]") && this.dialog?.open) this.close();
    });

    if (!this.dialog) return;
    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener("close", () => this.setStatus(""));
    this.dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.close();
    });

    document.addEventListener("submit", async (event) => {
      const form = event.target.closest("[data-bag-form]");
      if (!form || form.dataset.ready === "true") return;
      event.preventDefault();
      if (this.busy || form.dataset.saving === "true") return;
      form.dataset.saving = "true";
      const saved = await this.saveGift(form);
      delete form.dataset.saving;
      if (!saved) return;
      if (!form.querySelector("[data-gift]").checked) {
        form.querySelectorAll("[data-gift-fields] input, [data-gift-fields] textarea").forEach((field) => (field.value = ""));
      }
      form.dataset.ready = "true";
      form.requestSubmit(event.submitter || form.querySelector('[name="checkout"]'));
      delete form.dataset.ready;
    });

    // Gift details: show the fields with the box, and keep them on the cart as they're typed.
    document.addEventListener("change", (event) => {
      const form = event.target.closest("[data-bag-form]");
      if (!form) return;
      if (event.target.matches("[data-gift]")) {
        form.querySelector("[data-gift-fields]").hidden = !event.target.checked;
      }
      if (event.target.name?.startsWith("attributes[")) this.saveGift(form);
    });
  },

  open() {
    if (!this.dialog.open) this.dialog.showModal();
  },

  async close() {
    const form = this.dialog.querySelector("[data-bag-form]");
    if (form && !(await this.saveGift(form))) return;
    this.dialog.close();
  },

  setStatus(html) {
    const status = (this.dialog?.open ? this.dialog : document.querySelector("[data-bag-page]") || this.dialog)?.querySelector("[data-bag-status]");
    if (status) status.innerHTML = html;
  },

  /** Puts Shopify's freshly rendered drawer in place and updates the header count. */
  render(sections) {
    const html = sections?.[SECTION];
    if (!html || !this.dialog) return;
    const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("[data-bag-body]");
    const body = this.dialog.querySelector("[data-bag-body]");
    if (fresh && body) body.replaceWith(fresh);
    const count = fresh?.querySelector("[data-bag-count-source]")?.textContent ?? "0";
    document.querySelectorAll("[data-bag-count]").forEach((el) => (el.textContent = count));
  },

  /** Adds a product form's contents. Resolves to true when it went in. */
  async add(formData) {
    if (this.busy) return false;
    this.busy = true;
    formData.append("sections", SECTION);
    formData.append("sections_url", window.location.pathname);
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      this.render(data.sections);
      const variant = data.variant_title && !data.product_has_only_default_variant ? `, ${escapeHtml(data.variant_title)}` : "";
      this.setStatus(
        `<p role="status" class="border-b border-heath px-6 py-3 text-small font-bold text-heath">${strings.addedToBag}: <span class="font-display font-normal">${escapeHtml(data.product_title)}</span>${variant}</p>`,
      );
      if (this.dialog) this.open();
      else window.location.href = routes.cart;
      return true;
    } catch (error) {
      this.setStatus(`<p role="alert" class="border-b border-heath px-6 py-3 text-small font-bold">${escapeHtml(error.message || strings.addFailed)}</p>`);
      if (this.dialog) this.open();
      return false;
    } finally {
      this.busy = false;
    }
  },

  /** Sets a line's quantity; 0 removes it. */
  async change(key, quantity) {
    if (this.busy) return;
    this.busy = true;
    const page = document.querySelector("[data-bag-page]");
    const body = this.dialog?.open ? this.dialog.querySelector("[data-bag-body]") : page || this.dialog?.querySelector("[data-bag-body]");
    body?.setAttribute("aria-busy", "true");
    try {
      const form = body?.querySelector("[data-bag-form]");
      if (form && !(await this.saveGift(form))) return;
      const response = await fetch(`${routes.cartChange}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity, sections: [SECTION], sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      if (page) window.location.reload();
      else this.render(data.sections);
    } catch (error) {
      this.setStatus(`<p role="alert" class="border-b border-heath px-6 py-3 text-small font-bold">${escapeHtml(error.message || strings.addFailed)}</p>`);
    } finally {
      body?.removeAttribute("aria-busy");
      this.busy = false;
    }
  },

  saveGift(form) {
    const attributes = {};
    for (const [name, value] of new FormData(form)) {
      const key = name.match(/^attributes\[(.+)\]$/)?.[1];
      if (key) attributes[key] = value;
    }
    if (attributes.Gift !== "Yes") {
      attributes.Gift = "";
      attributes["Gift recipient"] = "";
      attributes["Gift note"] = "";
    }
    const body = JSON.stringify({ attributes });
    this.giftSave = (this.giftSave || Promise.resolve())
      .catch(() => {})
      .then(async () => {
        const response = await fetch(`${routes.cartUpdate}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body,
          keepalive: true,
        });
        if (!response.ok) throw new Error(strings.addFailed);
      });
    return this.giftSave.then(() => true).catch((error) => {
      this.setStatus(`<p role="alert" class="px-6 py-3">${escapeHtml(error.message)}</p>`);
      return false;
    });
  },
};

function escapeHtml(text) {
  const span = document.createElement("span");
  span.textContent = text ?? "";
  return span.innerHTML;
}

/* ------------------------------------------------------------------------------------------------
 * Quantity steppers (QuantityStepper.tsx). In the bag they change the line; in a product form
 * they set the quantity field and announce the change.
 * ---------------------------------------------------------------------------------------------- */

function initSteppers() {
  document.addEventListener("click", (event) => {
    const step = event.target.closest("[data-step]");
    if (!step) return;
    const stepper = step.closest("[data-stepper]");
    const output = stepper.querySelector("[data-value]");
    const min = Number(stepper.dataset.min);
    const max = Number(stepper.dataset.max);
    const value = Math.min(max, Math.max(min, Number(output.textContent) + Number(step.dataset.step)));

    if (stepper.dataset.line) {
      bag.change(stepper.dataset.line, value);
      return;
    }
    output.textContent = value;
    const input = stepper.querySelector("[data-input]");
    if (input) input.value = value;
    stepper.querySelector('[data-step="-1"]').disabled = value <= min;
    stepper.querySelector('[data-step="1"]').disabled = value >= max;
    stepper.dispatchEvent(new CustomEvent("quantity", { bubbles: true, detail: value }));
  });
}

/* Quick add on product cards (QuickAdd.tsx). */
function initQuickAdd() {
  document.addEventListener("submit", (event) => {
    const form = event.target.closest("[data-quick-add]");
    if (!form) return;
    event.preventDefault();
    bag.add(new FormData(form));
  });
}

/* ------------------------------------------------------------------------------------------------
 * Product page (PurchasePanel.tsx): size, flavour picks, live price and "Add to bag".
 * ---------------------------------------------------------------------------------------------- */

/** What a customer must still do before this pick can go in the bag, or null (pickHint in catalog.ts). */
function pickHint(min, max, count) {
  const word = (n) => ["no", "one", "two", "three", "four", "five", "six"][n] ?? String(n);
  const noun = (n) => (n === 1 ? "flavour" : "flavours");
  if (min === max) return count === min ? null : `Choose ${word(min)} ${noun(min)}.`;
  if (count < min) return `Choose at least ${word(min)} ${noun(min)}.`;
  if (count > max) return `Choose up to ${word(max)} ${noun(max)}.`;
  return null;
}

function initProductForm(form) {
  const data = JSON.parse(form.querySelector("[data-product-data]").textContent);
  const price = form.querySelector("[data-price]");
  const comparePrice = form.querySelector("[data-compare-price]");
  const comparePriceWrap = form.querySelector("[data-compare-price-wrap]");
  const button = form.querySelector("[data-add]");
  const note = form.querySelector("[data-delivery-note]");
  const picker = form.querySelector("[data-picker]");
  const boxes = [...form.querySelectorAll("[data-flavour]")];
  const flavoursInput = form.querySelector("[data-flavours-input]");
  const variantInputs = [...form.querySelectorAll("[data-variant]")];
  const deliveryText = note?.textContent;
  let quantity = 1;
  let addedTimer;

  const variant = () => {
    const id = Number(variantInputs.find((i) => i.checked)?.value ?? form.querySelector('[name="id"]').value);
    return data.variants.find((v) => v.id === id) ?? data.variants[0];
  };

  /** The defaults that fit this size, so switching sizes never leaves an impossible pick. */
  const startPicks = () => {
    const v = variant();
    const picks = v.max && data.defaults.length <= v.max ? data.defaults : [];
    boxes.forEach((box) => (box.checked = picks.includes(box.value)));
  };

  const update = () => {
    const v = variant();
    const single = v.max === 1;
    boxes.forEach((box) => (box.type = single ? "radio" : "checkbox"));
    boxes.forEach((box) => (box.name = single ? "flavour-pick" : ""));
    const picked = boxes.filter((box) => box.checked);
    const hint = picker && v.max ? pickHint(v.min, v.max, picked.length) : null;

    if (picker && v.max) {
      boxes.forEach((box) => {
        box.disabled = !single && !box.checked && picked.length >= v.max;
        box.closest("label").querySelector("[data-chosen]").classList.toggle("invisible", !box.checked);
      });
      const word = ["no", "one", "two", "three", "four", "five", "six"][v.max] ?? v.max;
      picker.querySelector("[data-pick-legend]").textContent = single
        ? strings.chooseYourFlavour
        : v.min === v.max
          ? `Choose ${word} flavours`
          : `Choose up to ${word} flavours`;
      picker.querySelector("[data-pick-status]").textContent =
        hint ?? (single ? strings.flavourChosen : format(strings.ofChosen, { count: picked.length, max: v.max }));
      flavoursInput.value = listNames(picked.map((box) => box.dataset.name));
      flavoursInput.disabled = picked.length === 0;
    }

    price.textContent = formatPrice(v.price * quantity);
    if (comparePrice && comparePriceWrap) {
      const onSale = v.compareAtPrice > v.price;
      comparePriceWrap.hidden = !onSale;
      comparePrice.textContent = onSale ? formatPrice(v.compareAtPrice * quantity) : "";
    }
    const soldOut = !v.available;
    button.disabled = soldOut || hint !== null;
    if (!addedTimer) button.textContent = soldOut ? button.dataset.soldOutLabel : button.dataset.label;
    if (note && deliveryText) note.textContent = deliveryText;
  };

  const resetAdded = () => {
    window.clearTimeout(addedTimer);
    addedTimer = undefined;
  };

  variantInputs.forEach((input) =>
    input.addEventListener("change", () => {
      resetAdded();
      startPicks();
      update();
    }),
  );
  boxes.forEach((box) =>
    box.addEventListener("change", () => {
      resetAdded();
      update();
    }),
  );
  form.addEventListener("quantity", (event) => {
    quantity = event.detail;
    update();
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (button.disabled) return;
    button.disabled = true;
    const ok = await bag.add(new FormData(form));
    button.disabled = false;
    if (ok) {
      resetAdded();
      button.textContent = form.dataset.addedLabel;
      addedTimer = window.setTimeout(() => {
        addedTimer = undefined;
        update();
      }, 2400);
    }
    update();
  });

  startPicks();
  update();
}

initHeader();
initMenu();
bag.init();
initSteppers();
initQuickAdd();
document.querySelectorAll("[data-product-form]").forEach(initProductForm);

/* Keep the Shop disclosure dismissible with the keyboard and outside clicks. */
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  document.querySelectorAll("[data-shop-dropdown][open]").forEach((menu) => {
    menu.open = false;
    menu.querySelector("summary").focus();
  });
});
document.addEventListener("click", (event) => {
  document.querySelectorAll("[data-shop-dropdown][open]").forEach((menu) => {
    if (!menu.contains(event.target)) menu.open = false;
  });
});
